import React from 'react';
import { Link } from 'react-router-dom';
import CopyPrompt from '../Common/CopyPrompt';

const ITHelpdeskAgentPage = () => {
    const sampleQueries = [
        "How do I connect my Wi-Fi? Can you give me relevant articles to guide me?",
        "I still can't connect to my Wi-Fi. Can you create a ticket for me?"
    ];

    return (
        <div className="min-h-screen bg-purple-50 pb-20">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
                {/* Header */}
                <div className="text-center mb-8 sm:mb-12">
                    <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                        IT Workforce
                    </h1>
                    <p className="text-base sm:text-lg md:text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed px-2">
                        In this lab, you'll experience how an AI agent can act as your virtual IT Workforce assistant, 
                        guiding you through common technical issues and even creating support tickets automatically when needed.
                    </p>
                    <p className="text-base sm:text-lg md:text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed px-2 mt-4">
                        You'll play the role of a new student who just arrived on campus and is trying to connect to the university Wi-Fi. 
                        Let's see how AI can make your onboarding easier!
                    </p>

                    <div className="mt-6 mb-4">
                        <a 
                            href="https://app.campusmind.ai/agents-market" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-block bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-800 transition-all duration-200 shadow-lg hover:shadow-xl text-base sm:text-lg"
                        >
                            Try IT Workforce Now →
                        </a>
                    </div>
                </div>

                {/* Step 1: Access the IT Workforce Assistant */}
                <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-8 border-l-8 border-blue-500">
                    <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center text-white shadow-lg">
                                <span className="text-2xl font-bold">1</span>
                            </div>
                        </div>
                        <div className="flex-1">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">Access the IT Workforce Assistant</h2>
                            <p className="text-gray-700 mb-4">Start by locating and opening the IT Workforce (HelpDesk) assistant.</p>
                            
                            <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mb-4">
                                <h3 className="font-semibold text-blue-900 mb-2">Steps:</h3>
                                <ul className="text-blue-800 space-y-1">
                                    <li>• Go to the <strong>Agent Marketplace</strong> from the left navigation pane</li>
                                    <li>• Under the <strong>IT Support</strong> category, find and select <strong>"IT HelpDesk (ServiceNow) Agent"</strong></li>
                                    <li>• This agent connects to the university's <strong>ServiceNow</strong> system to manage IT knowledge articles and support tickets</li>
                                    <li>• It can both provide solutions and escalate unresolved issues by automatically creating help tickets</li>
                                    <li>• Once selected, click <strong>"Open Chat"</strong> to begin your conversation</li>
                                </ul>
                            </div>

                            {/* Screenshot for Step 1 */}
                            <div className="flex justify-center">
                                <img
                                    src="/step1-access-helpdesk-agent.png"
                                    alt="Accessing IT Workforce Assistant - Step 1"
                                    className="w-full max-w-2xl rounded-lg shadow-md"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Step 2: Chat with the Agent */}
                <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-8 border-l-8 border-green-500">
                    <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                            <div className="w-12 h-12 bg-gradient-to-br from-green-600 to-green-700 rounded-full flex items-center justify-center text-white shadow-lg">
                                <span className="text-2xl font-bold">2</span>
                            </div>
                        </div>
                        <div className="flex-1">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">Chat with the Agent</h2>
                            <p className="text-gray-700 mb-6">Now, interact with the IT Workforce assistant to explore its capabilities.</p>
                            
                            {/* Query 1: Get Wi-Fi Setup Instructions */}
                            <div className="bg-green-50 border-2 border-green-300 rounded-lg p-6 mb-6">
                                <h3 className="text-xl font-bold text-green-900 mb-4">Query 1: Get Wi-Fi Setup Instructions</h3>
                                <p className="text-green-800 mb-4">Ask:</p>
                                <div className="bg-white border border-green-200 rounded-lg p-4 mb-4">
                                    <p className="text-gray-800 italic">"How do I connect my Wi-Fi? Can you give me relevant articles to guide me?"</p>
                                </div>
                                
                                <div className="space-y-3 mb-4">
                                    <div className="flex items-start gap-2">
                                        <span className="text-green-600 font-bold">✅</span>
                                        <p className="text-green-800">The agent will:</p>
                                    </div>
                                    <ul className="text-green-800 space-y-1 ml-6">
                                        <li>• Search the ServiceNow Knowledge Base for relevant articles (e.g., <em>Connecting to Campus Wi-Fi</em>, <em>Troubleshooting Wi-Fi Access</em>)</li>
                                        <li>• Display article links directly in the chat</li>
                                        <li>• Provide step-by-step setup instructions or quick guides</li>
                                    </ul>
                                </div>
                                
                                <div className="bg-green-100 border border-green-400 rounded-lg p-3">
                                    <p className="text-green-800 text-sm">
                                        <strong>💡 Tip:</strong> Click one of the links to view the full article in ServiceNow — this simulates real integration with enterprise knowledge systems.
                                    </p>
                                </div>
                            </div>

                            {/* Screenshot for Query 1 */}
                            <div className="flex justify-center mb-6">
                                <img
                                    src="/step2-query1-wifi.png"
                                alt="Wi-Fi setup query - Step 2 Query 1"
                                    className="w-full max-w-2xl rounded-lg shadow-md"
                                />
                            </div>

                            {/* Query 2: Escalate to IT Support */}
                            <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-6 mb-6">
                                <h3 className="text-xl font-bold text-orange-900 mb-4">Query 2: Escalate to IT Support</h3>
                                <p className="text-orange-800 mb-4">If you're still unable to connect, ask:</p>
                                <div className="bg-white border border-orange-200 rounded-lg p-4 mb-4">
                                    <p className="text-gray-800 italic">"I still can't connect to my Wi-Fi. Can you create a ticket for me?"</p>
                                </div>
                                
                                <div className="space-y-3 mb-4">
                                    <div className="flex items-start gap-2">
                                        <span className="text-orange-600 font-bold">✅</span>
                                        <p className="text-orange-800">The agent will:</p>
                                    </div>
                                    <ul className="text-orange-800 space-y-1 ml-6">
                                        <li>• Automatically create a <strong>ServiceNow support ticket</strong> on your behalf</li>
                                        <li>• Return a <strong>ticket confirmation number</strong> (e.g., <code>INC1234567</code>)</li>
                                        <li>• Provide a <strong>link to view or track the ticket status</strong></li>
                                    </ul>
                                </div>
                                
                                <div className="bg-orange-100 border border-orange-400 rounded-lg p-3">
                                    <p className="text-orange-800 text-sm">
                                        <strong>💡 Tip:</strong> Click the ticket link to open ServiceNow and see how the issue is logged — including <strong>category</strong>, <strong>priority</strong>, and <strong>short description</strong>.
                                    </p>
                                </div>
                            </div>

                            {/* Screenshot for Query 2 */}
                            <div className="flex justify-center">
                                <img
                                    src="/step2-query2-ticket.png"
                                alt="Ticket creation query - Step 2 Query 2"
                                    className="w-full max-w-2xl rounded-lg shadow-md"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Step 3: Verify the Ticket */}
                <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-8 border-l-8 border-purple-500">
                    <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                            <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-purple-700 rounded-full flex items-center justify-center text-white shadow-lg">
                                <span className="text-2xl font-bold">3</span>
                            </div>
                        </div>
                        <div className="flex-1">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">Verify the Ticket</h2>
                            <p className="text-gray-700 mb-4">After your ticket is created, note the ticket number and confirmation message.</p>
                            
                            <div className="bg-purple-50 border-2 border-purple-300 rounded-lg p-4">
                                <h3 className="font-semibold text-purple-900 mb-2">Verify:</h3>
                                <ul className="text-purple-800 space-y-1">
                                    <li>• Issue summary and current status (e.g., <em>Open</em>, <em>In Progress</em>)</li>
                                    <li>• Assigned technician or support team managing your case</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Step 4: Reflection */}
                <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-8 border-l-8 border-teal-500">
                    <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                            <div className="w-12 h-12 bg-gradient-to-br from-teal-600 to-teal-700 rounded-full flex items-center justify-center text-white shadow-lg">
                                <span className="text-2xl font-bold">4</span>
                            </div>
                        </div>
                        <div className="flex-1">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">Reflection</h2>
                            
                            <div className="bg-teal-50 border-2 border-teal-300 rounded-lg p-4">
                                <h3 className="font-semibold text-teal-900 mb-2">🎯 What you learned:</h3>
                                <ul className="text-teal-800 space-y-1">
                                    <li>• How an AI agent integrates with enterprise systems like <strong>ServiceNow</strong></li>
                                    <li>• How the agent can <strong>search, retrieve, and escalate</strong> issues automatically</li>
                                    <li>• How AI streamlines IT support workflows — reducing wait times and improving student experience</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Wrap-Up */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl shadow-xl p-8 text-white mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-center">Wrap-Up</h2>
                    <p className="text-center text-blue-100 mb-4 text-lg leading-relaxed">
                        Perfect! You've just seen how the IT Workforce assistant can:
                    </p>
                    <ul className="text-center text-blue-100 text-lg leading-relaxed space-y-1 mb-4">
                        <li>• Guide you with <strong>knowledge base articles</strong></li>
                        <li>• Escalate unresolved issues automatically</li>
                        <li>• Create <strong>ServiceNow tickets</strong> with real tracking numbers</li>
                    </ul>
                    <p className="text-center text-blue-100 text-lg leading-relaxed">
                        This shows how AI agents can bridge <strong>self-service</strong> and <strong>human support</strong>, 
                        giving students instant help while ensuring critical issues reach IT staff.
                    </p>
                    <div className="text-center mt-6">
                        <span className="text-3xl">🎉</span>
                        <span className="text-2xl font-bold ml-2">That's all for this lab — great work!</span>
                        <span className="text-3xl ml-2">🚀</span>
                    </div>
                </div>

                {/* Navigation */}
                <div className="flex justify-center gap-4 flex-wrap">
                    <Link
                        to="/accessibility-agent"
                        className="bg-gray-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-700 transition-colors duration-200"
                    >
                        Back: Accessibility Agent
                    </Link>
                    <Link
                        to="/recruiting-workforce"
                        className="bg-purple-700 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-800 transition-colors duration-200"
                    >
                        Next: Recruiting Workforce
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default ITHelpdeskAgentPage;
